/**
 * AlertHistoryTable — paginated Chakra Table of AdminAlertHistoryEntry rows.
 * Renders a Simulation column using a distinct badge and includes the core
 * triage columns (region, organization, location, started/ended, duration).
 *
 * 014-alerts / US7 / T132.
 */
import {
  Badge,
  Box,
  Button,
  Flex,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import type { AdminAlertHistoryEntry, AdminAlertHistoryPage } from '../api/adminAlertsApi';

interface Props {
  page: AdminAlertHistoryPage | undefined;
  isLoading: boolean;
  onPageChange: (page: number) => void;
}

export function AlertHistoryTable({ page, isLoading, onPageChange }: Props) {
  const { t } = useTranslation();

  if (isLoading && !page) {
    return (
      <Box p={6}>
        <Text>{t('common.loading', 'Loading…')}</Text>
      </Box>
    );
  }

  const items = page?.items ?? [];
  const currentPage = page?.page ?? 1;
  const pageSize = page?.pageSize ?? 50;
  const total = page?.totalCount ?? 0;
  const lastPage = Math.max(1, Math.ceil(total / pageSize));

  return (
    <Box>
      <Box overflowX="auto">
        <Table size="sm" variant="simple">
          <Thead>
            <Tr>
              <Th>{t('admin.alerts.history.col.startedAt', 'Started')}</Th>
              <Th>{t('admin.alerts.history.col.region', 'Region')}</Th>
              <Th>{t('admin.alerts.history.col.type', 'Type')}</Th>
              <Th>{t('admin.alerts.history.col.organization', 'Organization')}</Th>
              <Th>{t('admin.alerts.history.col.location', 'Location')}</Th>
              <Th>{t('admin.alerts.history.col.duration', 'Duration')}</Th>
              <Th>{t('admin.alerts.history.col.playbackAction', 'Playback')}</Th>
              <Th>{t('admin.alerts.history.col.simulation', 'Simulation')}</Th>
            </Tr>
          </Thead>
          <Tbody>
            {items.length === 0 ? (
              <Tr>
                <Td colSpan={8}>
                  <Text py={4} color="gray.500" textAlign="center">
                    {t('admin.alerts.history.empty', 'No alerts in this window.')}
                  </Text>
                </Td>
              </Tr>
            ) : (
              items.map((row) => (
                <Tr key={row.alertEventId} data-testid={`alert-history-row-${row.alertEventId}`}>
                  <Td>{formatDateTime(row.startedAt)}</Td>
                  <Td>{row.regionName ?? row.regionId}</Td>
                  <Td>{row.alertType}</Td>
                  <Td>{row.organizationName ?? row.organizationId}</Td>
                  <Td>{row.locationName ?? row.locationId}</Td>
                  <Td>{formatDuration(row)}</Td>
                  <Td>
                    <Badge colorScheme={playbackActionColor(row.playbackAction)}>
                      {row.playbackAction}
                    </Badge>
                  </Td>
                  <Td>
                    {row.isSimulated ? (
                      <Badge colorScheme="orange" data-testid="alert-history-simulated-badge">
                        {row.simulationAutoEnded
                          ? t('admin.alerts.history.simulationAutoEnded', 'SIMULATION (auto)')
                          : t('admin.alerts.history.simulation', 'SIMULATION')}
                      </Badge>
                    ) : (
                      <Text as="span" color="gray.400">
                        —
                      </Text>
                    )}
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </Box>

      <Flex justify="space-between" align="center" mt={4}>
        <Text fontSize="sm" color="gray.600">
          {t('admin.alerts.history.pageInfo', 'Page {{page}} of {{last}} · {{total}} events', {
            page: currentPage,
            last: lastPage,
            total,
          })}
        </Text>
        <Flex gap={2}>
          <Button
            size="sm"
            onClick={() => onPageChange(currentPage - 1)}
            isDisabled={currentPage <= 1 || isLoading}
          >
            {t('common.previous', 'Previous')}
          </Button>
          <Button
            size="sm"
            onClick={() => onPageChange(currentPage + 1)}
            isDisabled={currentPage >= lastPage || isLoading}
          >
            {t('common.next', 'Next')}
          </Button>
        </Flex>
      </Flex>
    </Box>
  );
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString();
}

function formatDuration(row: AdminAlertHistoryEntry): string {
  if (row.durationSeconds == null) return '—';
  const m = Math.floor(row.durationSeconds / 60);
  const s = row.durationSeconds % 60;
  return `${m}m ${s.toString().padStart(2, '0')}s`;
}

function playbackActionColor(action: string): string {
  switch (action) {
    case 'played_alarm':
      return 'red';
    case 'silenced':
      return 'blue';
    case 'continued':
      return 'green';
    case 'skipped_outside_hours':
      return 'gray';
    case 'content_missing':
      return 'yellow';
    default:
      return 'gray';
  }
}
